// Client API des FICHES DE FABRICATION COSMÉTIQUES (/api/fiches-cosmetique).
// S'appuie sur le client générique `api()` (credentials inclus, erreurs typées).
// Miroir de src/lib/fiches.ts (savon), adapté aux tables cosmétiques dédiées (C4a).
// Les types d'ENTRÉE (create/update) sont importés du schéma Zod partagé (source unique) ;
// les types de SORTIE (exposés par l'API) sont définis ici.

import { api } from './api'
import type {
  FicheCosmetiqueCreateInput,
  FicheCosmetiqueUpdateInput,
} from '@shared/schemas/fiche-cosmetique'

export type FicheCosmetiqueStatut = 'en_cours' | 'libere' | 'non_conforme'

// Résumé renvoyé par la liste.
export interface FicheCosmetiqueResume {
  id: string
  numeroLot: string
  nomProduit: string | null
  dateFabrication: string | null
  statut: FicheCosmetiqueStatut
}

// Ligne « matière première » telle que renvoyée par l'API (traçabilité lot / DDM).
export interface LigneFicheCosmetique {
  id: string
  ordre: number | null
  phase: string | null
  designation: string
  inci: string | null
  fournisseur: string | null
  lotFournisseur: string | null
  ddm: string | null
  coutRecette: number | null
  qtePeseeG: number | null
  articleId: string | null
  fait: boolean
}

// Fiche complète (en-tête + snapshot figé + process + lignes).
export interface FicheCosmetiqueDetail extends FicheCosmetiqueResume {
  snapshotFormule: Record<string, unknown>
  process: Record<string, unknown>
  poidsTheoriqueG: number | null
  observations: string | null
  lignes: LigneFicheCosmetique[]
}

/** Liste les fiches de fabrication de la cliente (plus récentes d'abord). */
export async function fetchFichesCosmetique(): Promise<FicheCosmetiqueResume[]> {
  const { fiches } = await api<{ fiches: FicheCosmetiqueResume[] }>('/api/fiches-cosmetique')
  return fiches
}

/** Lit une fiche complète (avec ses lignes). */
export async function fetchFicheCosmetique(id: string): Promise<FicheCosmetiqueDetail> {
  const { fiche } = await api<{ fiche: FicheCosmetiqueDetail }>(`/api/fiches-cosmetique/${id}`)
  return fiche
}

/** Crée une fiche et renvoie sa version enregistrée. */
export async function creerFicheCosmetique(
  payload: FicheCosmetiqueCreateInput,
): Promise<FicheCosmetiqueDetail> {
  const { fiche } = await api<{ fiche: FicheCosmetiqueDetail }>('/api/fiches-cosmetique', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return fiche
}

/** Met à jour une fiche (remplissage progressif, statut, lignes) et la renvoie. */
export async function majFicheCosmetique(
  id: string,
  payload: FicheCosmetiqueUpdateInput,
): Promise<FicheCosmetiqueDetail> {
  const { fiche } = await api<{ fiche: FicheCosmetiqueDetail }>(`/api/fiches-cosmetique/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
  return fiche
}

/** Supprime une fiche de la cliente. */
export async function supprimerFicheCosmetique(id: string): Promise<void> {
  await api<{ ok: true }>(`/api/fiches-cosmetique/${id}`, { method: 'DELETE' })
}

/** Annule la libération d'un lot (repasse « en cours ») et renvoie la fiche. */
export async function annulerLiberationFicheCosmetique(
  id: string,
): Promise<FicheCosmetiqueDetail> {
  const { fiche } = await api<{ fiche: FicheCosmetiqueDetail }>(
    `/api/fiches-cosmetique/${id}/annuler-liberation`,
    { method: 'POST' },
  )
  return fiche
}
